import React, { useState, useCallback, useRef, useEffect } from 'react';
import './SpeechInputTestPage.css';

// Sample words for testing, grouped by CEFR level
const SAMPLE_WORDS: Record<string, string[]> = {
  A1: ['apple', 'book', 'cat', 'dog', 'eat', 'fish', 'good', 'house', 'ice', 'jump'],
  A2: ['beautiful', 'children', 'different', 'example', 'favorite', 'garden', 'hospital', 'important', 'kitchen', 'library'],
  B1: ['absolutely', 'comfortable', 'disappointed', 'environment', 'experience', 'furniture', 'government', 'immediately', 'knowledge', 'necessary'],
  B2: ['accommodate', 'circumstances', 'controversial', 'enthusiastic', 'extraordinary', 'nevertheless', 'opportunity', 'phenomenon', 'sophisticated', 'temperature'],
  C1: ['acquaintance', 'bureaucracy', 'conscientious', 'deteriorate', 'entrepreneur', 'infrastructure', 'miscellaneous', 'reconnaissance', 'surveillance', 'unprecedented'],
};

interface LetterEntry {
  letter: string;
  isCorrect: boolean | null;
  timestamp: number;
}

const SpeechInputTestPage: React.FC = () => {
  const [isListening, setIsListening] = useState(false);
  const [isSupported, setIsSupported] = useState(true);
  const [selectedLevel, setSelectedLevel] = useState<string>('A2');
  const [currentWord, setCurrentWord] = useState<string>('');
  // Committed letters (from final results only — stable, never revised)
  const [letters, setLetters] = useState<LetterEntry[]>([]);
  // Preview letters (from current interim — volatile, updates freely)
  const [previewLetters, setPreviewLetters] = useState<string[]>([]);
  const [history, setHistory] = useState<{ word: string; spelled: string; correct: boolean }[]>([]);
  const [interimTranscript, setInterimTranscript] = useState('');
  const [mode, setMode] = useState<'free' | 'quiz'>('quiz');
  const [stats, setStats] = useState({ correct: 0, total: 0 });
  const [customWord, setCustomWord] = useState('');
  const recognitionRef = useRef<any>(null);
  const isListeningRef = useRef(false);
  const shouldContinueRef = useRef(false);
  // Track how many final-result letters we've already committed
  const committedFromFinalsRef = useRef(0);

  // Check browser support
  useEffect(() => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setIsSupported(false);
    }
  }, []);

  // Pick a random word from the selected level
  const pickRandomWord = useCallback(() => {
    const words = SAMPLE_WORDS[selectedLevel];
    const word = words[Math.floor(Math.random() * words.length)];
    setCurrentWord(word);
    setLetters([]);
    setPreviewLetters([]);
    setInterimTranscript('');
    committedFromFinalsRef.current = 0;
  }, [selectedLevel]);

  // Initialize first word when switching to quiz mode or level
  useEffect(() => {
    if (mode === 'quiz') {
      pickRandomWord();
      setStats({ correct: 0, total: 0 });
      setHistory([]);
    } else {
      setLetters([]);
      setPreviewLetters([]);
      committedFromFinalsRef.current = 0;
    }
  }, [mode, pickRandomWord]);

  // Derived state
  const currentSpelled = letters.map(l => l.letter).join('');
  const targetLetters = currentWord.split('');
  const committedCount = letters.length;
  const isWordComplete = mode === 'quiz' && currentWord && committedCount >= currentWord.length;

  // Auto-evaluate when word is complete
  useEffect(() => {
    if (isWordComplete) {
      const isCorrect = currentSpelled === currentWord.toLowerCase();
      setStats(prev => ({
        correct: prev.correct + (isCorrect ? 1 : 0),
        total: prev.total + 1,
      }));
      setHistory(prev => [{
        word: currentWord,
        spelled: currentSpelled,
        correct: isCorrect,
      }, ...prev]);

      // Stop listening when complete
      shouldContinueRef.current = false;
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      setPreviewLetters([]);
    }
  }, [isWordComplete, currentSpelled, currentWord]);

  const startRecognition = useCallback(() => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition || isListeningRef.current) return;

    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 3;

    recognition.onstart = () => {
      isListeningRef.current = true;
      setIsListening(true);
      setInterimTranscript('');
    };

    recognition.onresult = (event: any) => {
      // 1) Collect ALL final-result text and current interim text
      let allFinalText = '';
      let currentInterimText = '';

      for (let i = 0; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript.toLowerCase();
        if (event.results[i].isFinal) {
          allFinalText += transcript;
        } else {
          currentInterimText += transcript;
        }
      }

      // 2) Extract a-z letters from finals
      const allFinalLetters = allFinalText.replace(/[^a-z]/g, '');
      const totalFinalCount = allFinalLetters.length;

      // 3) Commit any NEW final letters we haven't committed yet
      if (totalFinalCount > committedFromFinalsRef.current) {
        const newFinalLetters = allFinalLetters.slice(committedFromFinalsRef.current);
        committedFromFinalsRef.current = totalFinalCount;

        setLetters(prev => {
          const newEntries: LetterEntry[] = [];
          let nextIdx = prev.length;

          for (const letter of newFinalLetters) {
            let isCorrect: boolean | null = null;
            if (mode === 'quiz' && currentWord && nextIdx < currentWord.length) {
              isCorrect = letter === currentWord[nextIdx].toLowerCase();
            }
            newEntries.push({
              letter,
              isCorrect,
              timestamp: Date.now() + newEntries.length,
            });
            nextIdx++;
          }

          return newEntries.length > 0 ? [...prev, ...newEntries] : prev;
        });
      }

      // 4) Preview: extract a-z from current interim (volatile — can change freely)
      const interimLetters = currentInterimText.replace(/[^a-z]/g, '').split('').filter(Boolean);
      setPreviewLetters(interimLetters);
      setInterimTranscript(currentInterimText.trim());
    };

    recognition.onerror = (event: any) => {
      console.error('Speech recognition error:', event.error);
      if (event.error === 'no-speech') return;
      isListeningRef.current = false;
      setIsListening(false);
      if (event.error === 'not-allowed') {
        alert('麥克風權限被拒絕，請在瀏覽器設定中允許麥克風存取。');
        shouldContinueRef.current = false;
      }
    };

    recognition.onend = () => {
      isListeningRef.current = false;
      setIsListening(false);
      setPreviewLetters([]);
      // Auto-restart if we should still be listening
      if (shouldContinueRef.current) {
        // Reset final counter since new recognition instance starts fresh
        committedFromFinalsRef.current = 0;
        setTimeout(() => {
          if (shouldContinueRef.current) {
            startRecognition();
          }
        }, 100);
      }
    };

    recognitionRef.current = recognition;
    recognition.start();
  }, [mode, currentWord]);

  const startListening = useCallback(() => {
    if (shouldContinueRef.current) return;
    shouldContinueRef.current = true;
    startRecognition();
  }, [startRecognition]);

  const handleNextWord = useCallback(() => {
    shouldContinueRef.current = false;
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    pickRandomWord();
    setTimeout(() => {
      shouldContinueRef.current = true;
      startRecognition();
    }, 300);
  }, [pickRandomWord, startRecognition]);

  const handleBackspace = useCallback(() => {
    setLetters(prev => prev.slice(0, -1));
  }, []);

  const handleSetCustomWord = () => {
    if (customWord.trim()) {
      setCurrentWord(customWord.trim().toLowerCase());
      setCustomWord('');
      setLetters([]);
      setPreviewLetters([]);
      committedFromFinalsRef.current = 0;
      setMode('quiz');
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      shouldContinueRef.current = false;
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  if (!isSupported) {
    return (
      <div className="speech-test-page">
        <div className="speech-test-unsupported">
          <h2>Browser Not Supported</h2>
          <p>Web Speech API 不支援你目前的瀏覽器。</p>
          <p>請使用 <strong>Chrome</strong>、<strong>Edge</strong> 或 <strong>Safari</strong> 開啟此頁面。</p>
        </div>
      </div>
    );
  }

  // Build display: committed letters + preview letters
  const displayLetters: { letter: string; isCorrect: boolean | null; isPreview: boolean }[] = [];
  // Add committed
  for (const entry of letters) {
    displayLetters.push({ letter: entry.letter, isCorrect: entry.isCorrect, isPreview: false });
  }
  // Add preview (from interim, after committed)
  for (const pl of previewLetters) {
    displayLetters.push({ letter: pl, isCorrect: null, isPreview: true });
  }

  return (
    <div className="speech-test-page">
      <div className="speech-test-container">
        <h1>語音拼字測試</h1>
        <p className="speech-test-subtitle">
          一個字母一個字母唸，測試語音辨識拼字效果
        </p>

        {/* Mode Toggle */}
        <div className="speech-test-mode-toggle">
          <button
            className={`mode-btn ${mode === 'free' ? 'active' : ''}`}
            onClick={() => setMode('free')}
          >
            自由模式
          </button>
          <button
            className={`mode-btn ${mode === 'quiz' ? 'active' : ''}`}
            onClick={() => setMode('quiz')}
          >
            測驗模式
          </button>
        </div>

        {/* Quiz Mode: Level Selector & Target Word */}
        {mode === 'quiz' && (
          <div className="speech-test-quiz-section">
            <div className="level-selector">
              {Object.keys(SAMPLE_WORDS).map(level => (
                <button
                  key={level}
                  className={`level-btn ${selectedLevel === level ? 'active' : ''}`}
                  onClick={() => setSelectedLevel(level)}
                >
                  {level}
                </button>
              ))}
            </div>

            <div className="target-word-card">
              <span className="target-label">請一個字母一個字母唸出：</span>
              <span className="target-word">{currentWord}</span>
            </div>

            <div className="custom-word-input">
              <input
                type="text"
                placeholder="或輸入自訂單字..."
                value={customWord}
                onChange={e => setCustomWord(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSetCustomWord()}
              />
              <button onClick={handleSetCustomWord} disabled={!customWord.trim()}>
                設定
              </button>
            </div>
          </div>
        )}

        {/* Letter Tiles Display */}
        <div className="letter-tiles-section">
          <div className="letter-tiles">
            {mode === 'quiz' ? (
              targetLetters.map((expected, i) => {
                const display = displayLetters[i];
                const isCommitted = display && !display.isPreview;
                const isPreview = display && display.isPreview;
                const isCurrent = !display && i === displayLetters.length;

                let tileClass = 'letter-tile';
                if (isCommitted) {
                  tileClass += display.letter === expected.toLowerCase() ? ' correct' : ' incorrect';
                } else if (isPreview) {
                  tileClass += ' preview';
                } else if (isCurrent) {
                  tileClass += ' current';
                } else {
                  tileClass += ' pending';
                }

                return (
                  <div key={i} className={tileClass}>
                    <span className="tile-expected">{expected.toUpperCase()}</span>
                    {display && (
                      <span className={`tile-input ${isPreview ? 'preview-text' : display.letter === expected.toLowerCase() ? '' : 'wrong'}`}>
                        {display.letter.toUpperCase()}
                      </span>
                    )}
                    {isCurrent && isListening && (
                      <span className="tile-cursor">|</span>
                    )}
                  </div>
                );
              })
            ) : (
              displayLetters.length > 0 ? (
                displayLetters.map((display, i) => (
                  <div key={i} className={`letter-tile ${display.isPreview ? 'preview' : 'filled'}`}>
                    <span className={`tile-input ${display.isPreview ? 'preview-text' : 'free-input'}`}>
                      {display.letter.toUpperCase()}
                    </span>
                  </div>
                ))
              ) : (
                <div className="letter-tiles-placeholder">
                  開始說字母，會逐一顯示在這裡
                </div>
              )
            )}
          </div>
          {(committedCount > 0 || previewLetters.length > 0) && (
            <div className="spelled-word">
              拼出：<strong>{displayLetters.map(d => d.letter).join('')}</strong>
            </div>
          )}
        </div>

        {/* Microphone Button / Status */}
        <div className="speech-test-mic-section">
          {isWordComplete ? (
            <div className={`word-result ${currentSpelled === currentWord.toLowerCase() ? 'word-correct' : 'word-incorrect'}`}>
              {currentSpelled === currentWord.toLowerCase() ? 'Correct!' : `答案：${currentWord}`}
            </div>
          ) : !shouldContinueRef.current && !isListening ? (
            <button className="mic-btn" onClick={startListening}>
              <svg viewBox="0 0 24 24" width="48" height="48" fill="currentColor">
                <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm-1-9c0-.55.45-1 1-1s1 .45 1 1v6c0 .55-.45 1-1 1s-1-.45-1-1V5z"/>
                <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
              </svg>
            </button>
          ) : (
            <div className="listening-indicator">
              <span className="listening-dot" />
              <span className="listening-dot" />
              <span className="listening-dot" />
            </div>
          )}
          <p className="mic-hint">
            {isWordComplete
              ? '按「下一個」繼續'
              : shouldContinueRef.current || isListening
                ? `正在聆聽第 ${displayLetters.length + 1} 個字母...`
                : '點擊麥克風開始唸字母'}
          </p>
          {(shouldContinueRef.current || isListening) && !isWordComplete && (
            <div className="live-transcript-box">
              <span className="live-label">即時聽到：</span>
              <span className="live-text">{interimTranscript || '...'}</span>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="action-buttons">
          {committedCount > 0 && !isWordComplete && (
            <button className="backspace-btn" onClick={handleBackspace}>
              ← 刪除上一個
            </button>
          )}
          {isWordComplete && mode === 'quiz' && (
            <button className="next-word-btn" onClick={handleNextWord}>
              下一個單字
            </button>
          )}
          {committedCount > 0 && (
            <button
              className="clear-btn"
              onClick={() => {
                shouldContinueRef.current = false;
                if (recognitionRef.current) recognitionRef.current.stop();
                setLetters([]);
                setPreviewLetters([]);
                committedFromFinalsRef.current = 0;
              }}
            >
              清除
            </button>
          )}
        </div>

        {/* Stats (Quiz Mode) */}
        {mode === 'quiz' && stats.total > 0 && (
          <div className="speech-test-stats">
            <span className="stat-correct">{stats.correct} 正確</span>
            <span className="stat-divider">/</span>
            <span className="stat-total">{stats.total} 題</span>
            <span className="stat-rate">
              ({Math.round((stats.correct / stats.total) * 100)}%)
            </span>
          </div>
        )}

        {/* History (Quiz Mode) */}
        {history.length > 0 && (
          <div className="speech-test-results">
            <h3>歷史紀錄</h3>
            {history.map((h, i) => (
              <div key={i} className={`result-card ${h.correct ? 'correct' : 'incorrect'}`}>
                <div className="result-main">
                  <span className="result-transcript">
                    {h.word} → {h.spelled}
                  </span>
                  {h.correct
                    ? <span className="result-icon correct-icon">&#10003;</span>
                    : <span className="result-icon incorrect-icon">&#10007;</span>
                  }
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SpeechInputTestPage;
